import { useId, useState } from "react";
import { Button, StatusBadge, Textarea, useToast } from "@/design-system/components/ui";
import { extractErrorMessage } from "@/api/client";
import { useDateFormatter } from "@/utils/datetime";
import {
  useDiscardInterviewWordingDraft,
  useInterviewWordingHistory,
  usePreviewInterviewWording,
  useSaveInterviewWordingDraft,
} from "@/hooks/interviewWording/useInterviewWording";
import type { InterviewWordingItem, InterviewWordingPreview } from "@/types/interviewWording";
import {
  changeActionLabel,
  itemAccessibleName,
  itemStatus,
  KIND_LABEL,
  STATUS_LABEL,
} from "./labels";

const STATUS_TONE = { draft: "warn", published: "info", default: "neutral" } as const;

/**
 * One editable item: a question label, a hint, a text-box label, a choice, a
 * branch line, or a section's title or purpose.
 *
 * Words only. The option key is shown so the editor knows WHICH choice this is,
 * as muted text — never as a field. The count against `maxLength` is a hint; the
 * server is the authority on length and on every other rule, so nothing here
 * blocks a save on its own.
 *
 * The caller keys this component on the item's server state (draft and
 * published text), so the working text re-seeds when that state changes and is
 * left alone when another item is saved.
 */
export function ItemEditor({
  item,
  ownerId,
  reverting,
  onRevert,
}: {
  item: InterviewWordingItem;
  /** The question id or section id the item belongs to. */
  ownerId: string;
  reverting: boolean;
  onRevert: (item: InterviewWordingItem) => void;
}) {
  const toast = useToast();
  const seed = item.draftText ?? item.inForce;
  const [text, setText] = useState(seed);
  const [preview, setPreview] = useState<InterviewWordingPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logOpen, setLogOpen] = useState(false);

  const save = useSaveInterviewWordingDraft();
  const discard = useDiscardInterviewWordingDraft();
  const runPreview = usePreviewInterviewWording();

  const uid = useId();
  const countId = `${uid}-count`;
  const logId = `${uid}-log`;
  const name = itemAccessibleName(item, ownerId);
  const status = itemStatus(item);
  const over = text.length - item.maxLength;
  const blank = text.trim() === "";
  const changed = text !== seed;
  const rows = item.maxLength > 160 ? 4 : 2;

  const handleChange = (value: string) => {
    setText(value);
    setPreview(null);
    setError(null);
  };

  const handleSave = () => {
    setError(null);
    void save
      .mutateAsync({ blockKey: item.blockKey, text })
      .then(() => toast.success("Draft saved. The interview keeps its current wording until you publish."))
      .catch((e) => setError(extractErrorMessage(e)));
  };

  const handleDiscard = () => {
    setError(null);
    void discard
      .mutateAsync(item.blockKey)
      .then(() => toast.success("Draft discarded."))
      .catch((e) => setError(extractErrorMessage(e)));
  };

  const handlePreview = () => {
    setError(null);
    void runPreview
      .mutateAsync({ blockKey: item.blockKey, text })
      .then(setPreview)
      .catch((e) => setError(extractErrorMessage(e)));
  };

  return (
    <div
      role="group"
      aria-label={name}
      className="flex flex-col gap-space-2 rounded-md border border-border-light p-space-3"
    >
      <div className="flex flex-wrap items-center gap-space-2">
        <span className="text-small font-medium">{KIND_LABEL[item.kind]}</span>
        {item.optionKey ? (
          <span className="font-mono text-caption text-gray-mid">key: {item.optionKey}</span>
        ) : null}
        <StatusBadge tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</StatusBadge>
      </div>

      <Textarea
        value={text}
        rows={rows}
        aria-label={name}
        aria-describedby={countId}
        invalid={over > 0}
        onChange={(e) => handleChange(e.target.value)}
      />

      <div className="flex flex-wrap items-center gap-space-3">
        <span
          id={countId}
          data-over-cap={over > 0 ? "true" : "false"}
          className={over > 0 ? "text-caption text-status-error" : "text-caption text-gray-mid"}
        >
          {text.length} / {item.maxLength} characters
          {over > 0 ? ` · ${over} over the limit` : ""}
        </span>
        {blank ? (
          <span className="text-caption text-gray-mid">
            Empty — saving clears this item&apos;s draft; the wording in force stays.
          </span>
        ) : null}
      </div>

      <p className="m-0 text-caption text-gray-mid">
        <span className="font-medium">Default:</span> {item.defaultText}
      </p>
      {item.publishedText != null ? (
        <p className="m-0 text-caption text-gray-mid">
          <span className="font-medium">Published:</span> {item.publishedText}
        </p>
      ) : null}
      {item.appearsIn.length > 0 ? (
        <p className="m-0 text-caption text-gray-mid">Appears in: {item.appearsIn.join(", ")}</p>
      ) : null}
      {item.reminders.map((r) => (
        <p key={r} className="m-0 text-caption text-status-warning-ink">
          {r}
        </p>
      ))}

      <div className="flex flex-wrap items-center gap-space-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={handleSave}
          disabled={!changed || save.isPending}
          loading={save.isPending}
        >
          Save draft
        </Button>
        <Button
          size="sm"
          variant="text"
          onClick={handleDiscard}
          disabled={item.draftText == null || discard.isPending}
          loading={discard.isPending}
        >
          Discard draft
        </Button>
        <Button size="sm" variant="text" onClick={handlePreview} loading={runPreview.isPending}>
          Preview
        </Button>
        <Button
          size="sm"
          variant="text"
          onClick={() => onRevert(item)}
          // Only a published override has anything to revert: the confirmation
          // promises a new version, which a draft-only item would not get —
          // Discard draft is its control.
          disabled={reverting || item.publishedText == null}
        >
          Revert to default
        </Button>
        <Button
          size="sm"
          variant="text"
          aria-expanded={logOpen}
          aria-controls={logId}
          onClick={() => setLogOpen((o) => !o)}
        >
          Change log
        </Button>
      </div>

      {error ? (
        <p role="alert" className="m-0 whitespace-pre-line text-caption text-status-error">
          {error}
        </p>
      ) : null}

      {preview ? <PreviewResult preview={preview} /> : null}

      <div id={logId}>{logOpen ? <ChangeLog blockKey={item.blockKey} /> : null}</div>
    </div>
  );
}

function PreviewResult({ preview }: { preview: InterviewWordingPreview }) {
  return (
    <div
      className="flex flex-col gap-space-1 rounded-md bg-white-warm p-space-3"
      data-testid="interview-wording-preview"
    >
      {preview.problems.length > 0 ? (
        <>
          <p className="m-0 text-caption font-medium text-status-error">
            This wording would not publish:
          </p>
          <ul className="m-0 list-disc pl-space-4 text-caption text-status-error">
            {preview.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </>
      ) : (
        <p className="m-0 text-caption text-status-success">Nothing stops this wording from publishing.</p>
      )}
      {preview.sentences.map((s) => (
        <p key={`${s.surface}:${s.sentence}`} className="m-0 text-caption">
          How it reads in the {s.surface}: {s.sentence}
        </p>
      ))}
    </div>
  );
}

function ChangeLog({ blockKey }: { blockKey: string }) {
  const history = useInterviewWordingHistory(blockKey, true);
  const { dateTime } = useDateFormatter();

  if (history.data === undefined) {
    if (history.isError) {
      return (
        <p role="alert" className="m-0 text-caption text-status-error">
          {extractErrorMessage(history.error)}
        </p>
      );
    }
    return <p className="m-0 text-caption text-gray-mid">Loading the change log…</p>;
  }
  if (history.data.length === 0) {
    return <p className="m-0 text-caption text-gray-mid">No changes yet — the default has always been in force.</p>;
  }
  return (
    <ol className="m-0 flex list-none flex-col gap-space-2 p-0" aria-label="Change log">
      {history.data.map((c, i) => (
        <li key={`${c.atUtc}:${i}`} className="flex flex-col gap-space-1 border-t border-border-light pt-space-2">
          <span className="text-caption">
            <span className="font-medium">{changeActionLabel(c)}</span>
            {" · "}
            {c.userName ?? "—"}
            {" · "}
            {dateTime(c.atUtc)}
            {c.version != null ? ` · version ${c.version}` : ""}
          </span>
          <span className="text-caption text-gray-mid">Before: {c.before ?? "—"}</span>
          <span className="text-caption text-gray-mid">After: {c.after ?? "—"}</span>
        </li>
      ))}
    </ol>
  );
}
